import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _toString from "lodash/toString";
import _find from "lodash/find";
import _map from "lodash/map";
import _get from "lodash/get";
import "core-js/modules/es6.array.slice.js";
import "core-js/modules/es6.array.filter.js";
import "core-js/modules/es6.array.map.js";
import classNames from 'classnames';
import React, { useState } from 'react';
import { Impression } from '@nerdwallet/analytics/react/impression';
import { ElementImpression } from '@nerdwallet/nw-client-lib/tracking';
import { PRODUCT_TYPES } from '@nerdwallet/marketplace/constants';
import { getOfferId } from '@nerdwallet/marketplace/selectors/common';
import { Accent } from '@nerdwallet/currency';
import { ColumnHeader } from '../column-header/column-header';
import mapMarketplaceEntityToProps from '../mappings';
import { DEFAULT_DRIVER_GROUP } from '../../utils';
import { ENTITY_NAME, EVENT_TYPES } from '../../lib/analytics-constants';
import { CompareGridPaginationDesktop } from '../compare-grid-pagination/desktop/compare-grid-pagination-desktop';
import { CompareGridPaginationMobile } from '../compare-grid-pagination/mobile/compare-grid-pagination-mobile';
import { AddEntityToCompare } from '../add-entity-to-compare/add-entity-to-compare';
import CompareGridEmptyState from './compare-grid-empty-state';
import styles from '../comparison-grid.module.less';
export var CompareGridTabContent = function CompareGridTabContent(_ref) {
  var _classNames;

  var entitiesToCompare = _ref.entitiesToCompare,
      driverGroup = _ref.driverGroup,
      isMobile = _ref.isMobile,
      removeEntityClickHandler = _ref.removeEntityClickHandler,
      canAdd = _ref.canAdd,
      canRemove = _ref.canRemove,
      hideComparisonGridPanel = _ref.hideComparisonGridPanel,
      productType = _ref.productType,
      tabName = _ref.tabName,
      tabPosition = _ref.tabPosition,
      updateShouldShowSearch = _ref.updateShouldShowSearch,
      taxonomy = _ref.taxonomy,
      renderStickyBanner = _ref.renderStickyBanner,
      emptyState = _ref.emptyState,
      classes = _ref.classes;
  var MAX_ENTITIES_TO_SHOW = isMobile ? 2 : 4; // max entities to show in pagination

  /**
   * @type {[Boolean, Function]}
   * Show sticky column header on mobile if the user has scrolled down so that the full column header
   * is not visible (either it is only partially visible or has been fully scrolled past)
   */

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      showMobileStickyColumnHeader = _useState2[0],
      updateShowMobileStickyColumnHeader = _useState2[1];
  /**
   * @type {[Number, Function]} pagination item index
   */


  var _useState3 = useState(0),
      _useState4 = _slicedToArray(_useState3, 2),
      paginationItemIndex = _useState4[0],
      updatePaginationItemIndex = _useState4[1];
  /**
   * @type {[Boolean, Function]} show add entity button
   */


  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      enableAddEntity = _useState6[0],
      shouldEnableAddEntity = _useState6[1]; // if the tab is marked editable and add entity is enabled if you have reached the end of compare entities list,
  // or
  // if the tab is marked editable and pagination is not required then render Add entity component


  var shouldAddEntityRender = enableAddEntity || entitiesToCompare.length < MAX_ENTITIES_TO_SHOW;
  var shouldAddEntityOnFullGridRender = !canAdd ? shouldAddEntityRender && entitiesToCompare.length < MAX_ENTITIES_TO_SHOW : shouldAddEntityRender; // if there are no entities selected to compare, return an empty state

  if (!entitiesToCompare || entitiesToCompare.length < 1) {
    return /*#__PURE__*/React.createElement(CompareGridEmptyState, {
      canAdd: canAdd,
      hideComparisonGridPanel: hideComparisonGridPanel,
      productType: productType,
      shouldAddEntityRender: shouldAddEntityRender,
      tabName: tabName,
      tabPosition: tabPosition,
      updateShouldShowSearch: updateShouldShowSearch,
      emptyState: emptyState
    });
  }

  var handleScroll = function handleScroll(e) {
    var scrollTop = e.target.scrollTop;
    var shouldShowStickyColumnHeader = scrollTop > 0;
    updateShowMobileStickyColumnHeader(shouldShowStickyColumnHeader);
  };

  var currentEntities = entitiesToCompare.slice(paginationItemIndex, paginationItemIndex + MAX_ENTITIES_TO_SHOW); // map the driver values for the selected entities to compare

  var mappedEntitiesToCompare = _get(mapMarketplaceEntityToProps({
    currentEntities: currentEntities,
    driverGroup: driverGroup
  }), 'mappedEntities');

  var quickFactsTypes = _map(mappedEntitiesToCompare[0].drivers || [], 'id').filter(function (e) {
    return e !== 'chiclet';
  });
  /*
   * Returns the global position of the entity item given its relative position
   * @param {number} - position - the relative entity item index/position
   * @return {number}
   */


  var getGlobalEntityPosition = function getGlobalEntityPosition(position) {
    return paginationItemIndex + position + 1;
  };
  /*
   * Returns whether the current entity is the last one shown on the page, given its relative position
   * @param {number} - position - the relative entity index/position
   * @returns {boolean}
   */


  var getIsLastEntityOnPage = function getIsLastEntityOnPage(position) {
    return mappedEntitiesToCompare.length - 1 === position;
  };
  /*
   * Returns whether the current entity is the last entity in the entire list, given its relative position
   * @param {number} - position - the relative entity index/position
   * @returns {boolean}
   */


  var getIsLastEntityOverall = function getIsLastEntityOverall(position) {
    return getGlobalEntityPosition(position) === entitiesToCompare.length;
  };
  /*
   * Logic for rendering gradient, only on the last entity on the page if it's not the last entity overall and is not add entity
   * @param {number} - position - the relative entity index/position
   * @returns {boolean}
   */


  var getShowGradient = function getShowGradient(position) {
    return !shouldAddEntityRender && getIsLastEntityOnPage(position) && !getIsLastEntityOverall(position);
  };
  /*
   * Logic for checking if the current grid has the maximum number of entities, and the add entity icon is showing
   */


  var isComparisonGridFullWithAddEntity = shouldAddEntityRender && currentEntities.length === MAX_ENTITIES_TO_SHOW;
  /*
   * Returns the compare grid cell classname for either:
   * - max number of entities and displaying add entity
   * - not max number of entities and displaying add entity
   * - default cell style
   * Returns {string}
   */

  var getGridCellStyle = function getGridCellStyle() {
    if (isComparisonGridFullWithAddEntity) {
      return styles.compareGridCellWithAddEntityFull;
    }

    return shouldAddEntityRender && styles.compareGridCellWithAddEntity || styles.compareGridCellWidth;
  };
  /*
   * Renders the driver labels
   * @param {string} - factSlug - quick fact slug
   */


  var getDriverRowLabels = function getDriverRowLabels(factSlug) {
    return /*#__PURE__*/React.createElement("th", {
      key: "".concat(factSlug),
      className: classNames([styles.compareText__driver, getGridCellStyle(), classes === null || classes === void 0 ? void 0 : classes.titleCell])
    }, /*#__PURE__*/React.createElement(Accent, null, _find(mappedEntitiesToCompare[0].drivers, {
      id: factSlug
    }).label));
  };
  /*
   * Renders the driver values for each entity
   * @param {string} - factSlug - quick fact slug
   * @param {object} - entity - entity object
   */


  var getDriverRowValue = function getDriverRowValue(factSlug, entity, index) {
    var driverValue = _find(entity.drivers, {
      id: factSlug
    }).value;

    var driverToolTip = _find(entity.drivers, {
      id: factSlug
    }).valueTooltip;

    return /*#__PURE__*/React.createElement("td", {
      key: "".concat(factSlug, "-").concat(entity.id, "-").concat(index),
      className: classNames([styles.compareText__value, getGridCellStyle(), getShowGradient(index) && styles.cellGradient, classes === null || classes === void 0 ? void 0 : classes.valueCell])
    }, driverToolTip || driverValue);
  }; // check if this will work for all the verticals


  var offersIds = currentEntities.map(function (entity) {
    return getOfferId(entity);
  });
  var marketplaceOffersPayload = {
    section_name: tabName,
    section_position: tabPosition,
    additional_attributes: {
      marketplace_product_type: PRODUCT_TYPES.CREDIT_CARDS,
      marketplace_offers_count: currentEntities.length,
      marketplace_offer_ids: offersIds
    }
  };
  var paginationProps = {
    canAdd: canAdd,
    entitiesToCompare: entitiesToCompare,
    maxEntitiesToShow: MAX_ENTITIES_TO_SHOW,
    paginationItemIndex: paginationItemIndex,
    updatePaginationItemIndex: updatePaginationItemIndex,
    shouldEnableAddEntity: shouldEnableAddEntity,
    tabName: tabName,
    totalEntities: entitiesToCompare.length
  };
  return /*#__PURE__*/React.createElement(Impression, {
    entityName: ENTITY_NAME.COMPARISON_GRID_TAB_CONTENT,
    eventType: EVENT_TYPES.ELEMENT_IMPRESSION,
    payload: {
      element_name: ENTITY_NAME.COMPARISON_GRID_TAB_CONTENT,
      section_name: tabName,
      section_position: tabPosition
    }
  }, /*#__PURE__*/React.createElement(ElementImpression, {
    id: "".concat(ENTITY_NAME.COMPARISON_GRID_TAB_CONTENT, "_").concat(tabName),
    elementName: ENTITY_NAME.COMPARISON_GRID_TAB_CONTENT,
    sectionCategory: tabName,
    sectionPosition: tabPosition
  }, /*#__PURE__*/React.createElement(Impression, {
    className: classNames(styles.sideBySideCompareContainer, classes === null || classes === void 0 ? void 0 : classes.container),
    eventType: EVENT_TYPES.MARKETPLACE_OFFERS_IMPRESSION,
    entityName: ENTITY_NAME.COMPARISON_GRID_PRODUCT_ENTITIES,
    payload: marketplaceOffersPayload,
    onScroll: handleScroll
  }, /*#__PURE__*/React.createElement(ElementImpression, {
    id: "".concat(ENTITY_NAME.COMPARISON_GRID_PRODUCT_ENTITIES, "_").concat(tabName, "_").concat(_toString(offersIds)),
    elementName: ENTITY_NAME.COMPARISON_GRID_PRODUCT_ENTITIES,
    elementContent: _toString(offersIds),
    elementCount: currentEntities.length,
    sectionCategory: tabName,
    sectionPosition: tabPosition
  }, /*#__PURE__*/React.createElement("table", {
    className: classNames(styles.table, classes === null || classes === void 0 ? void 0 : classes.table)
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", {
    key: "column-header-row",
    className: classes === null || classes === void 0 ? void 0 : classes.headerRow
  }, mappedEntitiesToCompare.map(function (entity, index) {
    return /*#__PURE__*/React.createElement(ColumnHeader, {
      showGradient: getShowGradient(index),
      entity: entity,
      index: index,
      removeEntityClickHandler: removeEntityClickHandler,
      canRemove: canRemove,
      tabName: tabName,
      tabPosition: tabPosition,
      taxonomy: taxonomy,
      paginationItemIndex: paginationItemIndex,
      entityItemOverallPosition: getGlobalEntityPosition(index),
      updatePaginationItemIndex: updatePaginationItemIndex,
      shouldAddEntityRender: shouldAddEntityRender,
      className: classNames(getGridCellStyle(), classes === null || classes === void 0 ? void 0 : classes.headerCell),
      showMobileStickyColumnHeader: showMobileStickyColumnHeader,
      renderStickyBanner: renderStickyBanner,
      classes: classes
    });
  }), shouldAddEntityOnFullGridRender && /*#__PURE__*/React.createElement(AddEntityToCompare, {
    canAdd: canAdd,
    productType: productType,
    updateShouldShowSearch: updateShouldShowSearch,
    tabName: tabName,
    tabPosition: tabPosition,
    className: classNames((_classNames = {}, _defineProperty(_classNames, styles.addNewEntity__column, true), _defineProperty(_classNames, styles.addNewEntity__full, isComparisonGridFullWithAddEntity), _classNames)),
    shouldAddEntityRender: shouldAddEntityRender
  }))), /*#__PURE__*/React.createElement("tbody", null, quickFactsTypes.map(function (factSlug) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("tr", {
      key: "".concat(factSlug, "-title-row"),
      className: classNames(styles.driverTitleRow, classes === null || classes === void 0 ? void 0 : classes.titleRow)
    }, getDriverRowLabels(factSlug)), /*#__PURE__*/React.createElement("tr", {
      key: "".concat(factSlug, "-row"),
      className: classNames(styles.rowBottomBorders, classes === null || classes === void 0 ? void 0 : classes.valueRow)
    }, mappedEntitiesToCompare.map(function (entity, index) {
      return getDriverRowValue(factSlug, entity, index);
    }), shouldAddEntityOnFullGridRender && /*#__PURE__*/React.createElement("td", {
      className: styles.blankAddEntityCell
    })));
  }))))), /*#__PURE__*/React.createElement("div", {
    className: classNames(styles.compareGridFooter, classes === null || classes === void 0 ? void 0 : classes.footer)
  }, !isMobile ? /*#__PURE__*/React.createElement(CompareGridPaginationDesktop, paginationProps) : /*#__PURE__*/React.createElement(CompareGridPaginationMobile, paginationProps))));
};
CompareGridTabContent.defaultProps = {
  entitiesToCompare: [],
  driverGroup: DEFAULT_DRIVER_GROUP,
  canAdd: false,
  canRemove: false,
  isMobile: false
};